import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { toast } from "sonner";
import { AdminPageHeader } from "@/components/admin/crud-helpers";
import { StatsStrip } from "@/components/admin/stats-strip";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  ArrowLeft,
  Lock,
  Save,
  Image as ImageIcon,
  Inbox,
  Clock,
  CheckCircle2,
  AlertOctagon,
  Search,
  Send,
  Ticket,
} from "lucide-react";
import { AiAssistButton } from "@/components/ai/ai-assist-button";
import { formatDistanceToNow } from "date-fns";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import { PillTabsList, PillTabsTrigger } from "@/components/admin/pill-tabs";
import { Input } from "@/components/ui/input";
import { useServerFn } from "@tanstack/react-start";
import { saveTicketBranding } from "@/lib/email.functions";
import { MediaPickerDialog } from "@/components/admin/media-picker";

export const Route = createFileRoute("/_authenticated/admin/tickets")({ component: TicketsAdminWrapper });

function TicketsAdminWrapper() {
  return (
    <div className="space-y-6">
      <Tabs defaultValue="inbox">
        <PillTabsList search>
          <PillTabsTrigger value="inbox">Inbox</PillTabsTrigger>
          <PillTabsTrigger value="branding">Branding</PillTabsTrigger>
        </PillTabsList>
        <TabsContent value="inbox" className="mt-4"><TicketsAdmin /></TabsContent>
        <TabsContent value="branding" className="mt-4"><TicketBrandingEditor /></TabsContent>
      </Tabs>
    </div>
  );
}

type Ticket = {
  id: string;
  user_id: string | null;
  subject: string;
  body: string;
  status: "open" | "pending" | "resolved" | "closed";
  priority: "low" | "normal" | "high" | "urgent";
  assignee_id: string | null;
  created_at: string;
  updated_at: string;
};

type Reply = {
  id: string;
  ticket_id: string;
  author_id: string | null;
  body: string;
  is_internal: boolean;
  created_at: string;
};

const statusColors: Record<Ticket["status"], "default" | "secondary" | "outline"> = {
  open: "default",
  pending: "secondary",
  resolved: "outline",
  closed: "outline",
};

const priorityColors: Record<Ticket["priority"], string> = {
  low: "bg-muted text-muted-foreground",
  normal: "bg-blue-500/10 text-blue-600 dark:text-blue-400",
  high: "bg-orange-500/10 text-orange-600 dark:text-orange-400",
  urgent: "bg-destructive/10 text-destructive",
};

function TicketsAdmin() {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [priorityFilter, setPriorityFilter] = useState<string>("all");
  const [search, setSearch] = useState("");

  const { data: tickets, isLoading } = useQuery({
    queryKey: ["support_tickets", "all"],
    queryFn: async () => {
      const { data, error } = await supabase.from("support_tickets").select("*").order("updated_at", { ascending: false });
      if (error) throw error;
      return data as Ticket[];
    },
  });

  const stats = useMemo(() => {
    const list = tickets ?? [];
    return {
      total: list.length,
      open: list.filter((t) => t.status === "open").length,
      pending: list.filter((t) => t.status === "pending").length,
      resolved: list.filter((t) => t.status === "resolved" || t.status === "closed").length,
      urgent: list.filter((t) => t.priority === "urgent" && t.status !== "closed" && t.status !== "resolved").length,
    };
  }, [tickets]);

  const filtered = useMemo(() => {
    const list = tickets ?? [];
    return list.filter((t) => {
      if (statusFilter !== "all" && t.status !== statusFilter) return false;
      if (priorityFilter !== "all" && t.priority !== priorityFilter) return false;
      if (search) {
        const s = search.toLowerCase();
        if (!t.subject.toLowerCase().includes(s) && !(t.body ?? "").toLowerCase().includes(s)) return false;
      }
      return true;
    });
  }, [tickets, statusFilter, priorityFilter, search]);

  return (
    <div className="space-y-4">
      <AdminPageHeader title="Support tickets" description="Customer support inbox." />

      <StatsStrip
        tiles={[
          { label: "Total", value: stats.total, icon: Ticket },
          { label: "Open", value: stats.open, icon: Inbox, tone: stats.open ? "info" : "default" },
          { label: "Pending", value: stats.pending, icon: Clock, tone: stats.pending ? "warning" : "default" },
          { label: "Resolved", value: stats.resolved, icon: CheckCircle2, tone: "success" },
          { label: "Urgent open", value: stats.urgent, icon: AlertOctagon, tone: stats.urgent ? "danger" : "default" },
        ]}
      />

      <div className="grid gap-2 md:grid-cols-[1fr_auto_auto]">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search tickets…"
            className="pl-9"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="md:w-40"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="open">Open</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="resolved">Resolved</SelectItem>
            <SelectItem value="closed">Closed</SelectItem>
          </SelectContent>
        </Select>
        <Select value={priorityFilter} onValueChange={setPriorityFilter}>
          <SelectTrigger className="md:w-40"><SelectValue placeholder="Priority" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All priorities</SelectItem>
            <SelectItem value="low">Low</SelectItem>
            <SelectItem value="normal">Normal</SelectItem>
            <SelectItem value="high">High</SelectItem>
            <SelectItem value="urgent">Urgent</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="grid gap-3 lg:grid-cols-[380px_1fr]">
        <div className="space-y-2 lg:max-h-[calc(100vh-360px)] lg:overflow-y-auto lg:pr-1">
          {isLoading && (
            <div className="rounded-lg border border-border bg-card p-8 text-center text-sm text-muted-foreground">Loading…</div>
          )}
          {!isLoading && !filtered.length && (
            <div className="rounded-lg border border-dashed border-border bg-card p-8 text-center text-sm text-muted-foreground">
              No tickets match your filters.
            </div>
          )}
          {filtered.map((t) => {
            const active = selectedId === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setSelectedId(t.id)}
                className={`w-full rounded-lg border p-3 text-left transition-colors ${
                  active ? "border-primary bg-primary/5" : "border-border bg-card hover:bg-muted/50"
                }`}
              >
                <div className="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-2">
                  <div className="min-w-0">
                    <div className="truncate text-sm font-medium">{t.subject}</div>
                    <div className="mt-0.5 flex items-center gap-1.5 text-[11px] text-muted-foreground">
                      <Badge variant={statusColors[t.status]} className="text-[10px]">{t.status}</Badge>
                      <span className={`inline-flex rounded px-1.5 py-0.5 text-[10px] ${priorityColors[t.priority]}`}>
                        {t.priority}
                      </span>
                    </div>
                  </div>
                  <div className="shrink-0 text-right text-[11px] text-muted-foreground">
                    {formatDistanceToNow(new Date(t.updated_at), { addSuffix: true })}
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        <div className="min-w-0">
          {selectedId ? (
            <TicketDetail id={selectedId} onBack={() => setSelectedId(null)} />
          ) : (
            <div className="hidden h-full min-h-[400px] items-center justify-center rounded-lg border border-dashed border-border bg-card p-12 text-center text-sm text-muted-foreground lg:flex">
              <div>
                <Ticket className="mx-auto mb-3 h-10 w-10 opacity-40" />
                Select a ticket to view details.
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function TicketDetail({ id, onBack }: { id: string; onBack: () => void }) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [reply, setReply] = useState("");
  const [internal, setInternal] = useState(false);

  const { data: ticket } = useQuery({
    queryKey: ["support_tickets", id],
    queryFn: async () => {
      const { data, error } = await supabase.from("support_tickets").select("*").eq("id", id).single();
      if (error) throw error;
      return data as Ticket;
    },
  });

  const { data: replies } = useQuery({
    queryKey: ["ticket_replies", id],
    queryFn: async () => {
      const { data, error } = await supabase.from("ticket_replies").select("*").eq("ticket_id", id).order("created_at", { ascending: true });
      if (error) throw error;
      return data as Reply[];
    },
  });

  const updateStatus = useMutation({
    mutationFn: async (status: Ticket["status"]) => {
      const { error } = await supabase.from("support_tickets").update({ status }).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["support_tickets"] }); toast.success("Status updated"); },
    onError: (e: any) => toast.error(e.message),
  });

  const updatePriority = useMutation({
    mutationFn: async (priority: Ticket["priority"]) => {
      const { error } = await supabase.from("support_tickets").update({ priority }).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["support_tickets"] }); toast.success("Priority updated"); },
    onError: (e: any) => toast.error(e.message),
  });

  const sendReply = useMutation({
    mutationFn: async () => {
      if (!reply.trim()) throw new Error("Reply cannot be empty");
      const { error } = await supabase.from("ticket_replies").insert({
        ticket_id: id, author_id: user?.id, body: reply.trim(), is_internal: internal,
      });
      if (error) throw error;
      await supabase.from("support_tickets").update({ updated_at: new Date().toISOString() }).eq("id", id);
    },
    onSuccess: () => {
      setReply(""); setInternal(false);
      qc.invalidateQueries({ queryKey: ["ticket_replies", id] });
      qc.invalidateQueries({ queryKey: ["support_tickets"] });
      toast.success("Reply sent");
    },
    onError: (e: any) => toast.error(e.message),
  });

  if (!ticket) return <div className="rounded-lg border border-border bg-card p-8 text-center text-sm text-muted-foreground">Loading…</div>;

  return (
    <div className="space-y-4 rounded-lg border border-border bg-card p-4">
      <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3 sm:flex sm:flex-wrap">
        <div className="flex min-w-0 items-center gap-2">
          <Button variant="ghost" size="sm" onClick={onBack} className="lg:hidden">
            <ArrowLeft className="mr-1 h-4 w-4" /> Back
          </Button>
          <h2 className="truncate text-base font-semibold sm:text-lg">{ticket.subject}</h2>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Label className="text-xs text-muted-foreground">Status</Label>
          <Select value={ticket.status} onValueChange={(v) => updateStatus.mutate(v as Ticket["status"])}>
            <SelectTrigger className="h-8 w-32"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="open">Open</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="resolved">Resolved</SelectItem>
              <SelectItem value="closed">Closed</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <Label className="text-xs text-muted-foreground">Priority</Label>
          <Select value={ticket.priority} onValueChange={(v) => updatePriority.mutate(v as Ticket["priority"])}>
            <SelectTrigger className="h-8 w-32"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="low">Low</SelectItem>
              <SelectItem value="normal">Normal</SelectItem>
              <SelectItem value="high">High</SelectItem>
              <SelectItem value="urgent">Urgent</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="rounded-lg border border-border bg-muted/30 p-3">
        <div className="mb-2 text-xs text-muted-foreground">Opened {formatDistanceToNow(new Date(ticket.created_at), { addSuffix: true })}</div>
        <p className="whitespace-pre-wrap break-words text-sm">{ticket.body || <span className="text-muted-foreground">(no body)</span>}</p>
      </div>

      <div className="max-h-[360px] space-y-2 overflow-y-auto pr-1">
        {!replies?.length && (
          <div className="rounded-lg border border-dashed border-border p-4 text-center text-xs text-muted-foreground">No replies yet.</div>
        )}
        {replies?.map((r) => (
          <div key={r.id} className={`rounded-lg border p-3 ${r.is_internal ? "border-yellow-500/40 bg-yellow-500/5" : "border-border bg-background"}`}>
            <div className="mb-1 flex items-center gap-2 text-xs text-muted-foreground">
              {r.is_internal && <Lock className="h-3 w-3" />}
              {r.is_internal ? "Internal note" : "Reply"} · {formatDistanceToNow(new Date(r.created_at), { addSuffix: true })}
            </div>
            <p className="whitespace-pre-wrap break-words text-sm">{r.body}</p>
          </div>
        ))}
      </div>

      <div className="space-y-2 rounded-lg border border-border p-3">
        <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-2">
          <Label className="text-xs">New reply</Label>
          <AiAssistButton
            task="support_reply"
            context={`Subject: ${ticket.subject}\n\nCustomer message:\n${ticket.body}\n\nConversation so far:\n${(replies ?? []).map((r) => `${r.is_internal ? "[internal] " : ""}${r.body}`).join("\n---\n")}`}
            defaultPrompt={reply.trim() ? `Polish and complete this draft: ${reply}` : "Draft a helpful reply to this customer."}
            onApply={setReply}
            label="AI draft"
          />
        </div>
        <Textarea
          rows={4}
          value={reply}
          onChange={(e) => setReply(e.target.value)}
          placeholder="Write a response…"
          onKeyDown={(e) => {
            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
              e.preventDefault();
              sendReply.mutate();
            }
          }}
        />
        <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-2 sm:flex sm:justify-between">
          <div className="flex min-w-0 items-center gap-2">
            <Switch checked={internal} onCheckedChange={setInternal} />
            <Label className="truncate text-xs sm:text-sm">Internal note</Label>
          </div>
          <Button onClick={() => sendReply.mutate()} disabled={sendReply.isPending || !reply.trim()}>
            <Send className="mr-1 h-3.5 w-3.5" />
            {sendReply.isPending ? "Sending…" : "Send"}
          </Button>
        </div>
      </div>
    </div>
  );
}

type TicketBranding = {
  company_name: string;
  logo_url: string | null;
  support_email: string;
  phone: string;
  signature: string;
  accent_color: string;
  reply_footer: string;
  show_logo: boolean;
};

const defaultTicketBranding: TicketBranding = {
  company_name: "",
  logo_url: null,
  support_email: "",
  phone: "",
  signature: "Best regards,\nThe Support Team",
  accent_color: "#4378BC",
  reply_footer: "",
  show_logo: true,
};

function TicketBrandingEditor() {
  const qc = useQueryClient();
  const save = useServerFn(saveTicketBranding);
  const [b, setB] = useState<TicketBranding>(defaultTicketBranding);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["site_settings", "ticket_branding"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("site_settings").select("value").eq("key", "ticket_branding").maybeSingle();
      if (error) throw error;
      return (data?.value as TicketBranding) ?? null;
    },
  });

  useState(() => { if (data) setB({ ...defaultTicketBranding, ...data }); });
  // sync on data change
  if (data && b === defaultTicketBranding) setB({ ...defaultTicketBranding, ...data });

  if (isLoading) return <div className="p-8 text-center text-sm text-muted-foreground">Loading branding…</div>;

  const handleSave = async () => {
    setSaving(true);
    try {
      await save({ data: b });
      toast.success("Branding saved");
      qc.invalidateQueries({ queryKey: ["site_settings", "ticket_branding"] });
    } catch (e: any) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <div className="space-y-4 rounded-lg border border-border bg-card p-5">
        <h3 className="text-base font-semibold">Branding</h3>
        <div className="grid gap-3">
          <div>
            <Label>Company logo</Label>
            <div className="mt-1 flex items-center gap-3">
              {b.logo_url && <img src={b.logo_url} alt="logo" className="h-10 w-10 rounded border object-contain" />}
              <Button type="button" variant="outline" size="sm" onClick={() => setPickerOpen(true)}>
                <ImageIcon className="mr-1 h-4 w-4" /> {b.logo_url ? "Change" : "Pick"}
              </Button>
              {b.logo_url && <Button type="button" variant="ghost" size="sm" onClick={() => setB({ ...b, logo_url: null })}>Remove</Button>}
            </div>
          </div>
          <div><Label>Company name</Label><Input value={b.company_name} onChange={(e) => setB({ ...b, company_name: e.target.value })} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Support email</Label><Input type="email" value={b.support_email} onChange={(e) => setB({ ...b, support_email: e.target.value })} /></div>
            <div><Label>Phone</Label><Input value={b.phone} onChange={(e) => setB({ ...b, phone: e.target.value })} /></div>
          </div>
          <div>
            <Label>Accent color</Label>
            <div className="flex items-center gap-2">
              <Input type="color" value={b.accent_color} onChange={(e) => setB({ ...b, accent_color: e.target.value })} className="h-10 w-16 p-1" />
              <Input value={b.accent_color} onChange={(e) => setB({ ...b, accent_color: e.target.value })} className="font-mono" />
            </div>
          </div>
          <div><Label>Reply signature</Label><Textarea rows={3} value={b.signature} onChange={(e) => setB({ ...b, signature: e.target.value })} /></div>
          <div><Label>Reply footer</Label><Textarea rows={2} value={b.reply_footer} onChange={(e) => setB({ ...b, reply_footer: e.target.value })} /></div>
          <div className="flex items-center gap-2">
            <Switch checked={b.show_logo} onCheckedChange={(v) => setB({ ...b, show_logo: v })} />
            <Label>Show logo in emails</Label>
          </div>
        </div>
        <Button onClick={handleSave} disabled={saving}><Save className="mr-1 h-4 w-4" />{saving ? "Saving…" : "Save branding"}</Button>
      </div>

      <div className="space-y-2">
        <div className="text-xs font-medium text-muted-foreground">Live preview</div>
        <div className="overflow-hidden rounded-xl border border-border bg-white text-slate-900 shadow-sm">
          <div className="h-2" style={{ backgroundColor: b.accent_color }} />
          <div className="space-y-4 p-6">
            <div className="flex items-center gap-3">
              {b.show_logo && b.logo_url && <img src={b.logo_url} alt="" className="h-10 w-10 rounded object-contain" />}
              <div>
                <div className="font-semibold">{b.company_name || "Your company"}</div>
                <div className="text-xs text-slate-500">{b.support_email || "support@example.com"}{b.phone ? ` · ${b.phone}` : ""}</div>
              </div>
            </div>
            <hr />
            <p className="text-sm">Hi there,</p>
            <p className="text-sm">Thanks for reaching out — we've received your ticket and a team member will follow up shortly.</p>
            <pre className="whitespace-pre-wrap text-sm font-sans text-slate-700">{b.signature}</pre>
            {b.reply_footer && <div className="border-t pt-3 text-xs text-slate-500 whitespace-pre-wrap">{b.reply_footer}</div>}
          </div>
        </div>
      </div>

      <MediaPickerDialog
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        onSelect={(_file, url) => { setB({ ...b, logo_url: url }); setPickerOpen(false); }}
        accept="image/*"
      />
    </div>
  );
}
